<template>
  <div class="budget-container budget-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Budget']" />

    <div class="glass-tab-view glass-tab-view--flush budget-glass-shell">
      <div class="glass-tab-view__content budget-page-stack">
        <div class="budget-header-block">
          <div class="budget-header-row">
            <div class="budget-header-row__text">
              <h2 class="budget-title">Budget List</h2>
              <p class="budget-subtitle">View account totals for each fiscal-year budget</p>
            </div>
            <div class="budget-header-actions">
              <PrimaryButton
                label="Add New Budget"
                icon="bi bi-plus-lg"
                @click="goToNewBudget"
              />
            </div>
          </div>
        </div>

        <div class="budget-filter-panel glass-tab-panel--nested">
          <LabelDropdown
            label="Current Budget"
            select-id="budget-fiscal-year"
            v-model="selectedBudget"
            flex
            wide
            :searchable="false"
            :disabled="loadingBudgets || !budgetDropdownOptions.length"
            :options="budgetDropdownOptions"
            @update:model-value="loadBudgetData"
          />
        </div>

        <div class="budget-table-panel glass-tab-panel--nested">
          <h3 class="budget-table-heading">Accounts</h3>
          <div class="budget-table-section consultant-glass-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table budget-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="2" class="consultant-glass-table-no-records budget-no-records">
                        Loading budget data...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="2" class="consultant-glass-table-no-records budget-no-records">{{ error }}</td>
                    </tr>
                    <tr v-else-if="budgetData.length === 0">
                      <td colspan="2" class="consultant-glass-table-no-records budget-no-records">
                        No budget records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(row, idx) in budgetData"
                        :key="row.id || row.name || idx"
                        class="consultant-glass-table-row budget-table-row"
                        :style="{ '--budget-row-delay': `${0.04 + Math.min(idx, 14) * 0.025}s` }"
                      >
                        <td>{{ row.name || row.accountName }}</td>
                        <td class="budget-amount-cell">{{ formatCurrency(row.total) }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/Budget.css';
import '../styles/budget-glass.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import logger from '@/utils/logger';
import BudgetOverviewService from '@/services/budgetOverview.service.js';

export default {
  name: 'Budget',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    PrimaryButton,
  },
  data() {
    return {
      budgetOptions: [],
      selectedBudget: '',
      budgetData: [],
      loading: false,
      loadingBudgets: false,
      error: null,
    };
  },
  computed: {
    companyId() {
      const storeCompany = this.$store?.state?.companies?.currentCompany;
      if (storeCompany?.id) {
        return storeCompany.id;
      }

      const globalCompany = this.$store?.state?.currentCompany;
      if (globalCompany?.id) {
        return globalCompany.id;
      }

      const routeCompanyId = this.$route?.params?.companyId || this.$route?.params?.id;
      if (routeCompanyId) {
        return routeCompanyId;
      }

      const localCompanyId = localStorage.getItem('currentCompanyId');
      if (localCompanyId) {
        return localCompanyId;
      }

      return null;
    },
    budgetDropdownOptions() {
      if (this.loadingBudgets) {
        return [{ value: '', label: 'Loading budgets...', disabled: true }];
      }
      if (!this.budgetOptions.length) {
        return [{ value: '', label: 'No budgets available', disabled: true }];
      }
      return [
        { value: '', label: 'Select Budget', disabled: true },
        ...this.budgetOptions.map((budget) => ({
          value: String(budget.fiscalYearStart),
          label: budget.name,
        })),
      ];
    },
  },
  created() {
    this.initializeBudget();
  },
  methods: {
    async initializeBudget() {
      if (!this.companyId) {
        this.error = 'Company ID not available';
        return;
      }

      this.loadingBudgets = true;
      try {
        await this.loadBudgetOptions();
      } catch (err) {
        logger.error('Error initializing budget:', err);
        this.error = err.message || 'Failed to initialize budget';
      } finally {
        this.loadingBudgets = false;
      }
    },
    async loadBudgetOptions() {
      const response = await BudgetOverviewService.getFiscalYears(this.companyId);
      if (!response.success) {
        this.error = response.message || 'Failed to load budget options';
        return;
      }

      const fiscalYears = response.data?.fiscalYears || [];
      this.budgetOptions = fiscalYears;

      if (fiscalYears.length > 0) {
        const latest = fiscalYears[fiscalYears.length - 1];
        this.selectedBudget = latest.fiscalYearStart ? String(latest.fiscalYearStart) : '';
        if (this.selectedBudget) {
          await this.loadBudgetData();
        }
      }
    },
    async loadBudgetData() {
      if (!this.companyId || !this.selectedBudget || this.selectedBudget === '') {
        this.budgetData = [];
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const fiscalYearStart = Number(this.selectedBudget);
        const response = await BudgetOverviewService.getBudgetOverview(this.companyId, {
          fiscalYearStart,
          reportBasis: 'accrual',
        });

        if (!response.success) {
          this.error = response.message || 'Failed to load budget data';
          this.budgetData = [];
          return;
        }

        const data = response.data || {};
        this.budgetData = Array.isArray(data.rows) ? data.rows : [];
      } catch (err) {
        logger.error('Error loading budget data:', err);
        this.error = err.message || 'Error loading budget data';
        this.budgetData = [];
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      const numericValue = Number(value) || 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(numericValue);
    },
    goToNewBudget() {
      const companyId = this.companyId || this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/company/new-budget`);
    },
  },
};
</script>
